/**
 * Scientific 3D Asset Factory client: API calls (role-checked on the server) and live Firestore hooks.
 * Jobs/assets are written only by the worker; this module never writes Firestore directly.
 */
import { useEffect, useState } from 'react';
import { collection, doc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import api from '../config/axios';
import { db } from '../config/firebase';

export const SCI3D_STAGES = [
  { key: 'specification', label: 'Specification', description: 'Classify the topic, subject and required structures' },
  { key: 'sources', label: 'Trusted sources', description: 'Download authoritative scientific data (NIST, NASA JPL, HRA)' },
  { key: 'generate', label: 'Build 3D model', description: 'Blender builds geometry, 3 levels of detail and 6 previews' },
  { key: 'validate', label: 'Scientific validation', description: 'Re-measure the model against the source data + glTF validator' },
  { key: 'metadata', label: 'Metadata', description: 'Labelled parts, scale, sources and attribution' },
  { key: 'upload', label: 'Store in Firebase', description: 'Immutable, hash-checked files in Firebase Storage' },
  { key: 'register', label: 'Register', description: 'Registry record; approved assets join the 3D Asset Library' },
] as const;

export type Sci3dStageKey = (typeof SCI3D_STAGES)[number]['key'];
export type Sci3dStatus = 'GENERATING' | 'APPROVED' | 'NEEDS_REVIEW' | 'REJECTED' | 'AWAITING_SOURCE';
export type Sci3dFamily = 'built_in' | 'celestial_body' | 'molecule' | 'macromolecule' | 'organ' | 'procedural' | 'specimen';

export interface Sci3dResolution {
  method: 'built_in' | 'catalogue' | 'ai_verified' | 'ai_unverified' | 'approved_source' | 'unrouted' | string;
  family: Sci3dFamily;
  entity: string;
  topicKey: string;
  identifiers?: Record<string, unknown>;
  wikidata?: { qid: string; label?: string; description?: string; url: string } | null;
  ai?: { provider: string; model: string; confidence?: number; reason?: string };
  aiError?: string;
  educationalStructures?: string[];
  searchTerms?: string[];
}

export interface Sci3dStageProgress {
  state?: 'running' | 'done' | 'skipped' | 'failed';
  startedAt?: number | null;
  finishedAt?: number | null;
  durationMs?: number | null;
  detail?: string | null;
  error?: string | null;
  substep?: { step: 'build' | 'lod' | 'preview'; n: number; of: number; view?: string; triangles?: number };
}

export interface Sci3dJob {
  assetId: string;
  status: Sci3dStatus;
  request: { topic: string; grade: number; curriculum: string; chapter?: string };
  prompt?: string;
  requestedBy?: string;
  completedStages: string[];
  runningStage?: string | null;
  progress?: Partial<Record<Sci3dStageKey, Sci3dStageProgress>>;
  lastError?: { stage: string; type: string } | null;
  haltReason?: string;
  halted?: boolean;
  scientificAccuracyScore?: number;
  geometryQualityScore?: number;
  blockingIssues?: string[];
  n8nExecutionId?: string;
  resolution?: Sci3dResolution | null;
  spec?: { family?: Sci3dFamily; canonicalName?: string; generationStrategy?: string };
  aiGenerated?: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface Sci3dMeshPart {
  name: string;
  label: string;
  ontologyId?: string;
  element?: string;
  meanRadiusKm?: number;
  semiMajorAxisAU?: number;
  triangles: number;
}

export interface Sci3dAsset {
  assetId: string;
  status: Sci3dStatus;
  published: boolean;
  title: string;
  subject: string;
  domain: string;
  grade: number;
  curriculum: string;
  thumbnailUrl?: string;
  previews?: Record<string, string | null>;
  modelSha256: string;
  meshParts: Sci3dMeshPart[];
  sources: { publisher: string; dataset: string; url: string; license: string; retrievedAt: string }[];
  attribution: string;
  validation: { status: Sci3dStatus; scientificAccuracyScore: number; geometryQualityScore: number; errors: string[] };
  missingRequiredStructures?: string[];
  scale?: Record<string, unknown>;
  lods?: { level: number; triangles: number; bytes: number }[];
  reviews?: { reviewerUid: string; decision: string; notes: string; decidedAt: number }[];
  libraryUrl?: string;
  requestedBy?: string;
  prompt?: string;
  n8nExecutionId?: string;
  family?: Sci3dFamily;
  aiGenerated?: boolean;
  scientificNote?: string | null;
  wikidata?: { qid: string; url: string; description?: string } | null;
  identifiers?: Record<string, unknown>;
  registeredAt: number;
}

export interface Sci3dCatalog {
  topics: { topic: string; subject: string; domain: string; autoApprovable: boolean; examplePrompt: string; aliases: string[] }[];
  families?: { family: Sci3dFamily; label: string; source: string; autoApprovable: boolean; examples: string[] }[];
  curricula: string[];
  note: string;
  n8nExecutionUrlTemplate?: string;
}

export interface Sci3dRequest {
  prompt: string;
  grade?: number;
  curriculum?: string;
  chapter?: string;
  chapterId?: string;
  topicId?: string;
}

function apiError(error: any, fallback: string): Error {
  return new Error(error?.response?.data?.error || error?.response?.data?.message || fallback);
}

export async function submitSci3dAsset(request: Sci3dRequest): Promise<{ assetId: string; status: Sci3dStatus }> {
  try {
    const { data } = await api.post('/sci3d/jobs', request);
    return data;
  } catch (error) {
    throw apiError(error, 'Could not start the generation');
  }
}

export async function resumeSci3dJob(assetId: string): Promise<void> {
  try {
    await api.post(`/sci3d/jobs/${encodeURIComponent(assetId)}/resume`);
  } catch (error) {
    throw apiError(error, 'Could not resume the job');
  }
}

export async function getSci3dModelUrls(assetId: string): Promise<Record<string, string>> {
  try {
    const { data } = await api.get(`/sci3d/assets/${encodeURIComponent(assetId)}/files`);
    return data.urls || {};
  } catch (error) {
    throw apiError(error, 'Could not load the model');
  }
}

export async function reviewSci3dAsset(assetId: string, decision: 'approve' | 'reject', glbSha256: string, notes: string) {
  try {
    const { data } = await api.post(`/sci3d/assets/${encodeURIComponent(assetId)}/review`, { decision, glbSha256, notes });
    return data.result;
  } catch (error) {
    throw apiError(error, 'Review was not recorded');
  }
}

export function useSci3dCatalog(): Sci3dCatalog | null {
  const [catalog, setCatalog] = useState<Sci3dCatalog | null>(null);
  useEffect(() => onSnapshot(doc(db, 'scientific_factory_config', 'catalog'),
    (snap) => setCatalog(snap.exists() ? (snap.data() as Sci3dCatalog) : null),
    () => setCatalog(null)), []);
  return catalog;
}

export function useSci3dJob(assetId: string | null): Sci3dJob | null {
  const [job, setJob] = useState<Sci3dJob | null>(null);
  useEffect(() => {
    setJob(null);
    if (!assetId) return undefined;
    return onSnapshot(doc(db, 'scientific_asset_jobs', assetId),
      (snap) => setJob(snap.exists() ? (snap.data() as Sci3dJob) : null), () => setJob(null));
  }, [assetId]);
  return job;
}

function useCollection<T>(name: string, orderField: string, max: number): { items: T[]; loading: boolean; error: string | null } {
  const [state, setState] = useState<{ items: T[]; loading: boolean; error: string | null }>({ items: [], loading: true, error: null });
  useEffect(() => onSnapshot(query(collection(db, name), orderBy(orderField, 'desc'), limit(max)),
    (snap) => setState({ items: snap.docs.map((d) => d.data() as T), loading: false, error: null }),
    (error) => setState({ items: [], loading: false, error: error.message })), [name, orderField, max]);
  return state;
}

export const useSci3dJobs = (max = 25) => useCollection<Sci3dJob>('scientific_asset_jobs', 'createdAt', max);
export const useSci3dAssets = (max = 100) => useCollection<Sci3dAsset>('scientific_assets', 'registeredAt', max);

// ---------------------------------------------------------------- AI settings (superadmin)

export type Sci3dProvider = 'openai' | 'codex' | 'claude' | 'gemini';
export interface Sci3dSettings { provider: Sci3dProvider; model: string; aiFallback: boolean }
export interface Sci3dProviderStatus { label: string; configured: boolean; defaultModel: string }

export async function getSci3dProviders(): Promise<{ providers: Record<Sci3dProvider, Sci3dProviderStatus>; settings: Sci3dSettings | null }> {
  try {
    const { data } = await api.get('/sci3d/ai/providers');
    return { providers: data.providers, settings: data.settings };
  } catch (error) {
    throw apiError(error, 'Could not load AI providers');
  }
}

export async function listSci3dModels(provider: Sci3dProvider): Promise<{ models: string[]; error: string | null }> {
  try {
    const { data } = await api.get('/sci3d/ai/models', { params: { provider } });
    return { models: data.models || [], error: data.error || null };
  } catch (error) {
    throw apiError(error, 'Could not list models');
  }
}

export async function testSci3dModel(provider: Sci3dProvider, model: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const { data } = await api.post('/sci3d/ai/test', { provider, model });
    return { ok: !!data.ok, error: data.error };
  } catch (error) {
    throw apiError(error, 'Test failed');
  }
}

export async function saveSci3dSettings(settings: Sci3dSettings): Promise<void> {
  try {
    await api.put('/sci3d/settings', settings);
  } catch (error) {
    throw apiError(error, 'Could not save settings');
  }
}

// ---------------------------------------------------------------- source approval (superadmin + associate)

export interface Sci3dCandidate {
  id: string; provider: string; title: string; unit?: string; recordLink?: string;
  thumbnail?: string; glbUrl: string; license: string; searchTerm?: string;
}
export interface Sci3dCandidateSet {
  topicKey: string; topic: string; entity: string; status: 'PENDING' | 'APPROVED' | 'REJECTED';
  candidates: Sci3dCandidate[]; ranking?: { provider: string; model: string; notes?: string } | null;
  searchTerms?: string[]; assetIds?: string[]; aiFallbackAvailable?: boolean; updatedAt: number;
}

export async function approveSci3dSource(topicKey: string, route: 'museum' | 'ai', candidateId?: string) {
  try {
    const { data } = await api.post(`/sci3d/sources/${encodeURIComponent(topicKey)}/approve`, { route, candidateId });
    return data as { resumed: number; warning?: string };
  } catch (error) {
    throw apiError(error, 'Could not approve the source');
  }
}

export async function rejectSci3dSource(topicKey: string) {
  try {
    await api.post(`/sci3d/sources/${encodeURIComponent(topicKey)}/reject`);
  } catch (error) {
    throw apiError(error, 'Could not reject the candidates');
  }
}

export function useSci3dCandidates(): { items: Sci3dCandidateSet[]; loading: boolean; error: string | null } {
  const [state, setState] = useState<{ items: Sci3dCandidateSet[]; loading: boolean; error: string | null }>({ items: [], loading: true, error: null });
  useEffect(() => onSnapshot(query(collection(db, 'scientific_source_candidates'), where('status', '==', 'PENDING'), limit(50)),
    (snap) => setState({ items: snap.docs.map((d) => d.data() as Sci3dCandidateSet).sort((a, b) => b.updatedAt - a.updatedAt), loading: false, error: null }),
    (error) => setState({ items: [], loading: false, error: error.message })), []);
  return state;
}
